/**
 * LegendItem Component
 * Renders a single icon + label row in the legend
 */

import { sanitizeSVG } from '@/lib/sanitize';
import type { LegendItem as LegendItemType } from './useLegendData';

interface LegendItemProps {
  item: LegendItemType;
  scale?: number;
}

// Base sizes that will be scaled
const BASE_SIZES = {
  iconSize: 20,
  gap: 8,
  padding: 3,
  fontSize: 9,
  letterSpacing: 0.3,
  abbrevIconSize: 18,
  abbrevFontSize: 7,
  fallbackIconSize: 14,
};

const CENTRALIZED_KEYPAD_FILL_COLOR = '#1d4ed8';
const CENTRALIZED_KEYPAD_BORDER_COLOR = '#1e3a8a';
const CENTRALIZED_KEYPAD_TEXT_COLOR = '#ffffff';

const isCentralizedKeypadLegendItem = (item: LegendItemType) =>
  item.key.endsWith(':centralized-keypad') || item.key.endsWith(':lv-keypad');

const getKeypadFontSize = (label: string, iconSize: number) => {
  if (label.length <= 2) return Math.max(8, iconSize * 0.45);
  if (label.length === 3) return Math.max(7, iconSize * 0.38);
  return Math.max(6, iconSize * 0.3);
};

/**
 * Constrain SVG to fit within container by adding width/height attributes
 * This ensures SVGs with large viewBoxes don't overflow
 */
function constrainSvgToFit(svg: string, size: number): string {
  if (!svg) return '';

  // Add width and height attributes to make SVG responsive to container
  // Only modify the <svg> element's attributes, not child elements like <rect>
  const constrained = svg.replace(/(<svg\s)([^>]*)(>)/i, (match, svgStart, attributes, svgEnd) => {
    // Remove existing width/height from svg attributes only
    const cleanedAttrs = attributes
      .replace(/\s*width\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\s*height\s*=\s*["'][^"']*["']/gi, '');
    return `${svgStart}width="${size}" height="${size}" style="max-width:${size}px;max-height:${size}px;display:block;" ${cleanedAttrs.trim()}${svgEnd}`;
  });

  return constrained;
}

export function LegendItem({ item, scale = 1 }: LegendItemProps) {
  // Calculate scaled sizes
  const iconSize = BASE_SIZES.iconSize * scale;
  const gap = BASE_SIZES.gap * scale;
  const padding = BASE_SIZES.padding * scale;
  const fontSize = BASE_SIZES.fontSize * scale;
  const letterSpacing = BASE_SIZES.letterSpacing * scale;
  const abbrevIconSize = BASE_SIZES.abbrevIconSize * scale;
  const abbrevFontSize = BASE_SIZES.abbrevFontSize * scale;
  const fallbackIconSize = BASE_SIZES.fallbackIconSize * scale;

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: `${gap}px`,
      padding: `${padding}px 0`,
    } as React.CSSProperties,
    iconContainer: {
      width: `${iconSize}px`,
      height: `${iconSize}px`,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden', // Prevent oversized SVGs from overflowing
    } as React.CSSProperties,
    label: {
      color: '#000000',
      textTransform: 'uppercase' as const,
      fontSize: `${fontSize}px`,
      letterSpacing: `${letterSpacing}px`,
      lineHeight: 1.3,
    } as React.CSSProperties,
  };
  const renderIcon = () => {
    if (isCentralizedKeypadLegendItem(item)) {
      const label = 'K1';
      const borderWidth = Math.max(2, iconSize * 0.08);

      return (
        <div
          style={{
            ...styles.iconContainer,
            backgroundColor: CENTRALIZED_KEYPAD_FILL_COLOR,
            border: `${borderWidth}px solid ${CENTRALIZED_KEYPAD_BORDER_COLOR}`,
            boxSizing: 'border-box',
            color: CENTRALIZED_KEYPAD_TEXT_COLOR,
            fontSize: `${getKeypadFontSize(label, iconSize)}px`,
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
      );
    }

    // If we have an SVG icon, render it with color injected and constrained to fit
    if (item.iconSvg) {
      // Inject the icon color into the SVG if it has a color
      let svgWithColor = item.iconSvg;
      if (item.iconColor || item.fillColor) {
        const color = item.fillColor || item.iconColor;
        // Replace fill="currentColor" or add fill attribute to paths/circles/etc
        svgWithColor = svgWithColor
          .replace(/fill="currentColor"/gi, `fill="${color}"`)
          .replace(/stroke="currentColor"/gi, `stroke="${color}"`)
          // If no fill is set on the svg element, add color style
          .replace(/<svg/i, `<svg style="color: ${color}"`);
      }

      // Constrain the SVG to fit within the icon container, then sanitize
      // before rendering. icon_svg is stored user/org content, so sanitize at
      // render time too (defense-in-depth), matching every other SVG sink.
      const constrainedSvg = sanitizeSVG(constrainSvgToFit(svgWithColor, iconSize));

      return (
        <div style={styles.iconContainer} dangerouslySetInnerHTML={{ __html: constrainedSvg }} />
      );
    }

    // If icon type is abbreviation, render a text-based icon
    if (item.iconType === 'abbreviation' && item.abbreviation) {
      const isCircle = item.iconShape === 'circle';
      const abbrevStyle: React.CSSProperties = {
        width: `${abbrevIconSize}px`,
        height: `${abbrevIconSize}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: item.fillColor || item.iconColor || '#6B7280',
        color: item.textColor || '#ffffff',
        fontSize: `${abbrevFontSize}px`,
        fontWeight: 600,
        borderRadius: isCircle ? '50%' : `${2 * scale}px`,
        border: '1px solid rgba(0,0,0,0.2)',
      };

      return <div style={abbrevStyle}>{item.abbreviation}</div>;
    }

    // Fallback: simple colored circle
    const fallbackStyle: React.CSSProperties = {
      width: `${fallbackIconSize}px`,
      height: `${fallbackIconSize}px`,
      borderRadius: '50%',
      backgroundColor: item.iconColor || '#6B7280',
      border: '1px solid rgba(0,0,0,0.2)',
    };

    return <div style={fallbackStyle} />;
  };

  return (
    <div style={styles.container}>
      {renderIcon()}
      <span style={styles.label}>{item.displayName}</span>
    </div>
  );
}
